// Who reacted and WHEN — port of ios/Sources/Views/ReactionDetailsSheet.swift (1.0.287): one row
// per person with the time they reacted (ReactionTime: «HH:mm» today, «d MMM · HH:mm» otherwise,
// nothing when unknown — never 1970), groups in the chips' order, people in reaction order with
// «Tú» IN PLACE (who reacted first is information), an unsynced reactor as «Alguien» so the
// dialog always matches the chip's number.
//
// WEB: tabs per emoji with their counts plus «Todas» (the Apple sheet is the «Todas» view), each
// person's avatar, and a click on a person opens their profile card.
import { useEffect, useMemo, useState } from 'react';
import { data } from '../../../bridge/client';
import { userDisplayName } from '../../../core/channelListDerivation';
import { ReactionTime } from '../../../core/quickReactions';
import { ReactionsDerivation, type ReactionDetailEntry, type ReactionDetailGroup } from '../../../core/reactionsDerivation';
import { locale, t } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { useLive, useMinuteClock } from '../../hooks';
import { Dialog } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { EmojiGlyph, type CustomEmojiMap } from './EmojiGlyph';

const ALL = '\u0000all';

export function ReactionDetailsDialog({ postId, initial, custom, onClose }: { postId: string; initial: string | null; custom: CustomEmojiMap; onClose(): void }) {
  const shell = useShell();
  const now = useMinuteClock();
  const { value } = useLive(() => data.call('reactionDetails', postId), ['postReactions', 'users', 'userStatuses'], [postId]);
  const usersById = useMemo(() => new Map((value?.users ?? []).map((u) => [u.id, u])), [value]);
  const groups: ReactionDetailGroup[] = useMemo(
    () =>
      value
        ? ReactionsDerivation.details(value.reactions, {
            usersById,
            currentUserId: value.currentUserId,
            youLabel: t('channel.reaction.you'),
            unknownLabel: t('channel.reaction.unknown'),
          })
        : [],
    [value, usersById],
  );
  const [tab, setTab] = useState<string>(initial ?? ALL);
  // The last reaction of a tab was removed while it was open: fall back to «Todas».
  useEffect(() => {
    if (value && tab !== ALL && !groups.some((g) => g.emojiName === tab)) setTab(ALL);
  }, [value, groups, tab]);
  // Every reaction gone (the post was cleared): nothing left to show.
  useEffect(() => {
    if (value && groups.length === 0) onClose();
  }, [value, groups.length, onClose]);

  const total = groups.reduce((n, g) => n + g.entries.length, 0);
  const shown = tab === ALL ? groups : groups.filter((g) => g.emojiName === tab);

  const person = (entry: ReactionDetailEntry, emoji: string | null, key: string) => {
    const user = usersById.get(entry.userId);
    const time = ReactionTime.label(entry.createAt, { now });
    return (
      <button
        key={key}
        type="button"
        className="rd-person"
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          onClose();
          shell.openUser({ userId: entry.userId }, { x: r.left + 24, y: r.top });
        }}
      >
        <Avatar userId={entry.userId} name={user ? userDisplayName(user) : entry.name} size={32} version={user?.lastPictureUpdate ?? 0} presence={value?.statuses[entry.userId] ?? null} />
        <span className="rd-name">{entry.name}</span>
        {time ? (
          <time className="rd-time" dateTime={new Date(entry.createAt).toISOString()} title={new Date(entry.createAt).toLocaleString(locale(), { dateStyle: 'full', timeStyle: 'short' })}>
            {time}
          </time>
        ) : null}
        {emoji ? (
          <span className="rd-emoji" title={`:${emoji}:`}>
            <EmojiGlyph name={emoji} custom={custom} />
          </span>
        ) : null}
      </button>
    );
  };

  return (
    <Dialog title={t('channel.reaction.details')} onClose={onClose} width={440}>
      <div className="rd-tabs" role="tablist">
        <button type="button" role="tab" className="rd-tab" aria-selected={tab === ALL} onClick={() => setTab(ALL)}>
          {t('web.emoji.reactions.all')} · {total}
        </button>
        {groups.map((g) => (
          <button key={g.emojiName} type="button" role="tab" className="rd-tab" aria-selected={tab === g.emojiName} onClick={() => setTab(g.emojiName)} title={`:${g.emojiName}:`}>
            <EmojiGlyph name={g.emojiName} custom={custom} />
            {g.entries.length}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {shown.map((g) => (
          <section key={g.emojiName}>
            {tab === ALL ? (
              <div className="rd-group-title">
                <EmojiGlyph name={g.emojiName} custom={custom} />
                <span>
                  :{g.emojiName}: · {g.entries.length}
                </span>
              </div>
            ) : null}
            {g.entries.map((entry) => person(entry, null, `${g.emojiName}-${entry.userId}`))}
          </section>
        ))}
      </div>
    </Dialog>
  );
}
